import { createReducer, on } from '@ngrx/store';
import { CACHE_TTL, isCacheStale } from '@ps/utils';
import { mapSurveyLocalization } from '@ps/survey-utils';

import { INITIAL_SURVEY_CREATION_REFERENCE_STATE } from '../survey-creation-state.constants';
import { SurveyCreationReferenceState } from '../survey-creation-state.model';
import {
  SurveyCreationReferenceActions,
  SurveyCreationReferenceApiActions,
} from './survey-creation-reference.actions';

export const surveyCreationReferenceReducer = createReducer(
  INITIAL_SURVEY_CREATION_REFERENCE_STATE,

  on(
    SurveyCreationReferenceApiActions.loadQualificationsSuccess,
    (
      state,
      { countryCode, languageShortCode, qualifications, cachedAt },
    ): SurveyCreationReferenceState => {
      const localeKey = mapSurveyLocalization(countryCode, languageShortCode);
      return {
        ...state,
        qualificationsRecord: {
          ...state.qualificationsRecord,
          [localeKey]: { data: qualifications, cachedAt },
        },
      };
    },
  ),

  on(
    SurveyCreationReferenceApiActions.loadQualificationsFailure,
    (
      state,
      { countryCode, languageShortCode },
    ): SurveyCreationReferenceState => {
      const localeKey = mapSurveyLocalization(countryCode, languageShortCode);
      const { [localeKey]: _removed, ...qualificationsRecord } =
        state.qualificationsRecord;
      return {
        ...state,
        qualificationsRecord,
      };
    },
  ),

  on(
    SurveyCreationReferenceActions.loadQuestionCategories,
    (state): SurveyCreationReferenceState => ({
      ...state,
      questionCategories: {
        ...state.questionCategories,
        loading: isCacheStale(
          state.questionCategories.cachedAt,
          CACHE_TTL.DEFAULT,
        ),
      },
    }),
  ),

  on(
    SurveyCreationReferenceApiActions.loadQuestionCategoriesSuccess,
    (state, { categories, cachedAt }): SurveyCreationReferenceState => ({
      ...state,
      questionCategories: { data: categories, cachedAt, loading: false },
    }),
  ),

  // Unlike the scalar reference resources, the timestamp is nulled so the next
  // drawer open retries instead of being gated out by a stale success stamp.
  on(
    SurveyCreationReferenceApiActions.loadQuestionCategoriesFailure,
    (state): SurveyCreationReferenceState => ({
      ...state,
      questionCategories: { data: [], cachedAt: null, loading: false },
    }),
  ),

  on(
    SurveyCreationReferenceApiActions.loadQuestionsStarted,
    (state, { key }): SurveyCreationReferenceState => ({
      ...state,
      questionsLoadingKey: key,
    }),
  ),

  on(
    SurveyCreationReferenceApiActions.loadQuestionsSuccess,
    (state, { key, questions, cachedAt }): SurveyCreationReferenceState => ({
      ...state,
      questionsRecord: {
        ...state.questionsRecord,
        [key]: { data: questions, cachedAt },
      },
      questionsLoadingKey: null,
    }),
  ),

  on(
    SurveyCreationReferenceApiActions.loadQuestionsFailure,
    (state, { key }): SurveyCreationReferenceState => {
      const { [key]: _removed, ...questionsRecord } = state.questionsRecord;
      return {
        ...state,
        questionsRecord,
        questionsLoadingKey: null,
      };
    },
  ),

  on(
    SurveyCreationReferenceApiActions.loadLocationConditionsStarted,
    (state, { key }): SurveyCreationReferenceState => ({
      ...state,
      locationConditionsLoadingKey: key,
    }),
  ),

  on(
    SurveyCreationReferenceApiActions.loadLocationConditionsSuccess,
    (state, { key, conditions, cachedAt }): SurveyCreationReferenceState => ({
      ...state,
      locationConditionsRecord: {
        ...state.locationConditionsRecord,
        [key]: { data: conditions, cachedAt },
      },
      locationConditionsLoadingKey: null,
    }),
  ),

  on(
    SurveyCreationReferenceApiActions.loadLocationConditionsFailure,
    (state, { key }): SurveyCreationReferenceState => {
      const { [key]: _removed, ...locationConditionsRecord } =
        state.locationConditionsRecord;
      return {
        ...state,
        locationConditionsRecord,
        locationConditionsLoadingKey: null,
      };
    },
  ),
);
